import ExcelJS from "exceljs";
import moment from "moment";
import { PERSONAL_ROUTE_STATUS, PICKUP_STATUS } from "../constants";

const TITLE_FILL = "FF0066B1";

/**
 * Per-group palette from departure.xlsx. Group label tile = dark color;
 * V No tile = light tint. Customer cells = white. Cycles after 10 groups.
 */
const CUSTOMER_BG = "FFFFFFFF";
const GROUP_STYLES = [
  { labelBg: "FFE06666", vehicleBg: "FFF4CCCC" },
  { labelBg: "FF6AA84F", vehicleBg: "FFD9EAD3" },
  { labelBg: "FFBF9000", vehicleBg: "FFFFF2CC" },
  { labelBg: "FF674EA7", vehicleBg: "FFD9D2E9" },
  { labelBg: "FFE69138", vehicleBg: "FFFCE5CD" },
  { labelBg: "FFA64D79", vehicleBg: "FFEAD1DC" },
  { labelBg: "FF45818E", vehicleBg: "FFD0E0E3" },
  { labelBg: "FFF46524", vehicleBg: "FFFFE8DE" },
  { labelBg: "FF4679CA", vehicleBg: "FFC9DAF8" },
  { labelBg: "FFCC4125", vehicleBg: "FFFFD4CC" },
];

/**
 * Minimum block count (4) keeps the sheet visually similar to the template even
 * when the largest group has fewer than 4 routes.
 */
const MIN_ROUTE_BLOCKS_PER_ROW = 4;

const LABEL_COL_WIDTH = 9.6640625;
const DIVIDER_COL_WIDTH = 1.1640625;
const VEHICLE_COL_WIDTH = 9.6640625;
const CUSTOMER_COL_WIDTH = 32.83203125;

function buildRouteColumnBlocks(blockCount) {
  const blocks = [];
  for (let i = 0; i < blockCount; i += 1) {
    const startCol = 2 + i * 3;
    blocks.push({
      dividerCol: startCol,
      vehicleCol: startCol + 1,
      customerCol: startCol + 2,
    });
  }
  return blocks;
}

function buildColumnWidths(blockCount) {
  const widths = [LABEL_COL_WIDTH];
  for (let i = 0; i < blockCount; i += 1) {
    widths.push(DIVIDER_COL_WIDTH, VEHICLE_COL_WIDTH, CUSTOMER_COL_WIDTH);
  }
  return widths;
}

const thinBorder = { style: "thin", color: { argb: "FF000000" } };
const DEFAULT_NAME_COLOR = "FF000000";
const DUPLICATE_NAME_COLOR = "FFFF0000";

function solidFill(argb) {
  return { type: "pattern", pattern: "solid", fgColor: { argb } };
}

function applyCellStyle(cell, {
  fillArgb,
  bold,
  fontSize = 11,
  fontColorArgb,
  wrap = true,
  hAlign = "center",
  vAlign = "middle",
  borderLeftRight = false,
  skipFont = false,
}) {
  if (fillArgb) cell.fill = solidFill(fillArgb);
  if (!skipFont) {
    const font = { size: fontSize, bold: !!bold };
    if (fontColorArgb) font.color = { argb: fontColorArgb };
    cell.font = font;
  }
  cell.alignment = { horizontal: hAlign, vertical: vAlign, wrapText: wrap };
  if (borderLeftRight) {
    cell.border = { left: thinBorder, right: thinBorder };
  }
}

function nameRichTextFont(isDuplicate, fontSize = 11) {
  return {
    size: fontSize,
    color: { argb: isDuplicate ? DUPLICATE_NAME_COLOR : DEFAULT_NAME_COLOR },
  };
}

function departureBoardCustomerKey(routeCustomer, customerById) {
  if (routeCustomer?.customer_id != null && routeCustomer.customer_id !== "") {
    return `id:${String(routeCustomer.customer_id)}`;
  }
  const name = departureBoardCustomerDisplayName(routeCustomer, customerById);
  return `name:${name}`;
}

/** Customers listed on more than one outbound route on this board. */
function buildDuplicateCustomerKeys(outboundRoutes, customerById) {
  const counts = new Map();
  const withEstimate = (outboundRoutes || []).filter((route) => {
    if (`${route?.type || ""}`.toLowerCase() !== "outbound") return false;
    if (!route?.estimated_start_time) return false;
    return moment(route.estimated_start_time).isValid();
  });

  for (const route of withEstimate) {
    for (const customer of route.route_customer_list || []) {
      if (!customerIncludedOnDepartureBoard(customer)) continue;
      const key = departureBoardCustomerKey(customer, customerById);
      counts.set(key, (counts.get(key) || 0) + 1);
    }
  }

  const duplicateKeys = new Set();
  counts.forEach((count, key) => {
    if (count > 1) duplicateKeys.add(key);
  });
  return duplicateKeys;
}

function formatLicensePlate(tag) {
  const t = `${tag || ""}`.trim();
  if (!t) return "";
  return t.startsWith("#") ? t : `#${t}`;
}

function parseLastFirstFallback(routeCustomer, customerMaster) {
  const legal = (`${customerMaster?.name || ""}`).trim();
  if (legal) return legal;
  const raw = (`${routeCustomer?.customer_name || ""}`).trim();
  const withoutParen = raw.replace(/\s*\([^)]*\)\s*$/, "").trim();
  return withoutParen || raw;
}

/** Chinese / preferred name (`name_cn`) only; fallback Last,First. */
export function departureBoardCustomerDisplayName(routeCustomer, customerById) {
  const master =
    routeCustomer?.customer_id != null
      ? customerById.get(String(routeCustomer.customer_id))
      : null;
  const cn = (`${master?.name_cn || ""}`).trim();
  if (cn) return cn;
  if (master) {
    const last = (`${master.lastname || ""}`).trim();
    const first = (`${master.firstname || ""}`).trim();
    const middle = (`${master.middle_name || ""}`).trim();
    const firstMiddle = [first, middle].filter(Boolean).join(" ");
    if (last && firstMiddle) return `${last},${firstMiddle}`;
    if (last) return last;
    if (firstMiddle) return firstMiddle;
    const legal = (`${master.name || ""}`).trim();
    if (legal) return legal;
  }
  return parseLastFirstFallback(routeCustomer, master);
}

function customerIncludedOnDepartureBoard(customer) {
  if (!customer) return false;
  if (customer.customer_pickup_status === PICKUP_STATUS.SCHEDULE_ABSENT) return false;
  if (customer.customer_route_status === PERSONAL_ROUTE_STATUS.SCHEDULED_ABSENT) return false;
  if (customer.customer_route_status === PERSONAL_ROUTE_STATUS.UNEXPECTED_ABSENT) return false;
  return true;
}

function formatEstimatedStartTimeLabel(estimatedStartTime) {
  if (!estimatedStartTime) return "";
  const m = moment(estimatedStartTime);
  if (!m.isValid()) return "";
  return m.format("h:mm A");
}

function estimatedStartTimeSortKey(estimatedStartTime) {
  const m = moment(estimatedStartTime);
  return m.isValid() ? m.valueOf() : Number.MAX_SAFE_INTEGER;
}

function buildVehicleInfoBlock(route, vehicles, getDriverName) {
  const vehicle = vehicles?.find((v) => v.id === route.vehicle);
  const vehicleNumber =
    vehicle?.vehicle_number != null && vehicle.vehicle_number !== ""
      ? String(vehicle.vehicle_number)
      : "";
  const driverName = getDriverName(route.driver) || "";
  const plate = formatLicensePlate(vehicle?.tag);
  return [vehicleNumber, driverName, plate].filter((line) => line !== "").join("\n");
}

function buildCustomerListCellValue(route, customerById, duplicateCustomerKeys) {
  const entries = (route.route_customer_list || [])
    .filter(customerIncludedOnDepartureBoard)
    .map((c) => ({
      name: departureBoardCustomerDisplayName(c, customerById),
      key: departureBoardCustomerKey(c, customerById),
    }))
    .filter((e) => e.name);

  if (entries.length === 0) return "";

  const hasDuplicate = entries.some((e) => duplicateCustomerKeys.has(e.key));
  if (!hasDuplicate) {
    return entries.map((e) => e.name).join(" / ");
  }

  const richText = [];
  entries.forEach((entry, index) => {
    if (index > 0) {
      richText.push({ text: " / ", font: nameRichTextFont(false) });
    }
    richText.push({
      text: entry.name,
      font: nameRichTextFont(duplicateCustomerKeys.has(entry.key)),
    });
  });
  return { richText };
}

/**
 * @param {object[]} outboundRoutes
 * @param {Map<string, object>} customerById
 * @param {object[]} vehicles
 * @param {(driverId: unknown) => string} getDriverName
 * @returns {{ timeLabel: string, sortKey: number, routes: object[] }[]}
 */
export function buildDepartureBoardTimeGroups(outboundRoutes, customerById, vehicles, getDriverName) {
  const withEstimate = (outboundRoutes || []).filter((route) => {
    if (`${route?.type || ""}`.toLowerCase() !== "outbound") return false;
    if (!route?.estimated_start_time) return false;
    return moment(route.estimated_start_time).isValid();
  });

  const byTime = new Map();
  for (const route of withEstimate) {
    const sortKey = estimatedStartTimeSortKey(route.estimated_start_time);
    const timeLabel = formatEstimatedStartTimeLabel(route.estimated_start_time);
    const bucketKey = String(sortKey);
    if (!byTime.has(bucketKey)) {
      byTime.set(bucketKey, { timeLabel, sortKey, routes: [] });
    }
    byTime.get(bucketKey).routes.push(route);
  }

  const groups = [...byTime.values()].sort((a, b) => a.sortKey - b.sortKey);
  for (const group of groups) {
    group.routes.sort((a, b) => {
      const va = vehicles?.find((v) => v.id === a.vehicle)?.vehicle_number ?? 0;
      const vb = vehicles?.find((v) => v.id === b.vehicle)?.vehicle_number ?? 0;
      return Number(va) - Number(vb);
    });
  }
  return groups;
}

function estimateDataRowHeight(routeSlots) {
  let maxLines = 3;
  for (const route of routeSlots) {
    if (!route) continue;
    const customers = (route.route_customer_list || []).filter(customerIncludedOnDepartureBoard).length;
    const approxLines = Math.max(3, Math.ceil(customers / 4));
    maxLines = Math.max(maxLines, approxLines);
  }
  return Math.min(120, Math.max(45, 12 + maxLines * 14));
}

function lastSheetColumn(blockCount) {
  return 1 + blockCount * 3;
}

function writeRouteDataRow(worksheet, rowNumber, groupLabel, routeSlots, style, ctx) {
  const labelCell = worksheet.getCell(rowNumber, 1);
  labelCell.value = groupLabel;
  applyCellStyle(labelCell, {
    fillArgb: style.labelBg,
    bold: true,
    fontSize: 12,
    fontColorArgb: "FFFFFFFF",
  });

  ctx.columnBlocks.forEach((block, index) => {
    const route = routeSlots[index];
    const dividerCell = worksheet.getCell(rowNumber, block.dividerCol);
    applyCellStyle(dividerCell, { fillArgb: CUSTOMER_BG, wrap: false });

    const vehicleCell = worksheet.getCell(rowNumber, block.vehicleCol);
    const customerCell = worksheet.getCell(rowNumber, block.customerCol);

    if (route) {
      vehicleCell.value = buildVehicleInfoBlock(route, ctx.vehicles, ctx.getDriverName);
      customerCell.value = buildCustomerListCellValue(
        route,
        ctx.customerById,
        ctx.duplicateCustomerKeys
      );
    } else {
      vehicleCell.value = "";
      customerCell.value = "";
    }

    applyCellStyle(vehicleCell, {
      fillArgb: route ? style.vehicleBg : CUSTOMER_BG,
      fontSize: 11,
    });
    const customerValueIsRichText =
      route &&
      customerCell.value &&
      typeof customerCell.value === "object" &&
      Array.isArray(customerCell.value.richText);
    applyCellStyle(customerCell, {
      fillArgb: CUSTOMER_BG,
      fontSize: 11,
      hAlign: "left",
      skipFont: customerValueIsRichText,
    });
  });

  worksheet.getRow(rowNumber).height = estimateDataRowHeight(routeSlots);
}

function writeSpacerRow(worksheet, rowNumber, blockCount) {
  const lastCol = lastSheetColumn(blockCount);
  worksheet.mergeCells(rowNumber, 1, rowNumber, lastCol);
  const cell = worksheet.getCell(rowNumber, 1);
  applyCellStyle(cell, { fillArgb: CUSTOMER_BG, wrap: false });
  worksheet.getRow(rowNumber).height = 8.25;
}

/**
 * @param {object} options
 * @param {object[]} options.outboundRoutes
 * @param {object[]} [options.customers]
 * @param {object[]} [options.vehicles]
 * @param {(driverId: unknown) => string} options.getDriverName
 * @param {Date|string} options.scheduleDate
 */
export async function buildDepartureBoardWorkbook({
  outboundRoutes,
  customers = [],
  vehicles = [],
  getDriverName,
  scheduleDate,
}) {
  const customerById = new Map();
  (customers || []).forEach((c) => {
    if (c?.id != null) customerById.set(String(c.id), c);
  });

  const groups = buildDepartureBoardTimeGroups(
    outboundRoutes,
    customerById,
    vehicles,
    getDriverName
  );

  const duplicateCustomerKeys = buildDuplicateCustomerKeys(outboundRoutes, customerById);

  const maxRoutesInAnyGroup = groups.reduce(
    (maxSoFar, group) => Math.max(maxSoFar, group.routes.length),
    0
  );
  const blockCount = Math.max(MIN_ROUTE_BLOCKS_PER_ROW, maxRoutesInAnyGroup);
  const columnBlocks = buildRouteColumnBlocks(blockCount);
  const columnWidths = buildColumnWidths(blockCount);
  const lastCol = lastSheetColumn(blockCount);

  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet("Departure Board");
  const sheetCtx = {
    vehicles,
    getDriverName,
    customerById,
    duplicateCustomerKeys,
    columnBlocks,
  };

  columnWidths.forEach((width, idx) => {
    worksheet.getColumn(idx + 1).width = width;
  });

  const dateLabel = moment(scheduleDate).format("MMMM DD, YYYY");
  let row = 1;

  worksheet.mergeCells(row, 1, row, lastCol);
  const titleCell = worksheet.getCell(row, 1);
  titleCell.value = `Vehicle Departure Board （${dateLabel}）`;
  applyCellStyle(titleCell, {
    fillArgb: TITLE_FILL,
    bold: true,
    fontSize: 16,
    fontColorArgb: "FFFFFFFF",
    vAlign: "middle",
  });
  for (let col = 2; col <= lastCol; col += 1) {
    applyCellStyle(worksheet.getCell(row, col), {
      fillArgb: TITLE_FILL,
      bold: true,
      fontSize: 16,
      fontColorArgb: "FFFFFFFF",
    });
  }
  worksheet.getRow(row).height = 47.25;
  row += 1;

  writeSpacerRow(worksheet, row, blockCount);
  row += 1;

  if (groups.length === 0) {
    worksheet.mergeCells(row, 1, row, lastCol);
    const emptyCell = worksheet.getCell(row, 1);
    emptyCell.value = "No outbound routes with an estimated start time for this date.";
    applyCellStyle(emptyCell, { fillArgb: "FFFFFFFF", hAlign: "left" });
    worksheet.getRow(row).height = 30;
    return workbook;
  }

  groups.forEach((group, groupIndex) => {
    const style = GROUP_STYLES[groupIndex % GROUP_STYLES.length];
    writeRouteDataRow(
      worksheet,
      row,
      `Group ${groupIndex + 1}`,
      group.routes,
      style,
      sheetCtx
    );
    row += 1;
    writeSpacerRow(worksheet, row, blockCount);
    row += 1;
  });

  return workbook;
}

/**
 * @param {object} options — same as buildDepartureBoardWorkbook
 * @param {string} [options.filenameBase]
 */
export async function downloadDepartureBoardExcel(options) {
  const workbook = await buildDepartureBoardWorkbook(options);
  const datePart = moment(options.scheduleDate).format("MM-DD-YYYY");
  const filenameBase = options.filenameBase || `Departure_Board_${datePart}`;
  const buffer = await workbook.xlsx.writeBuffer();
  const safe = `${filenameBase}`.replace(/[/\\?%*:|"<>]/g, "-");
  const filename = safe.toLowerCase().endsWith(".xlsx") ? safe : `${safe}.xlsx`;
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
